// What blocks Submit once the contract is editable, and what merely gets said.
//
// Two sources, and the split matters.
//
// The SCHEMA and COHERENCE rules come from the advanced editor's validator
// (validate.js:470) — no second mirror of strict mode lives here. Its problems are filtered to the
// output panel and then DIFFED against the source spec's own problems, because the form must not
// start refusing specs it duplicates happily today: a legacy source with a path that already
// resolved to nothing is not this operator's doing, and blocking on it would be a regression. A
// path THEY broke is a different thing, and it blocks.
//
// The rest are checks the validator cannot make because they are about the editor's own state, not
// the definition: a row typed but not named is dropped by `toSchema` and would vanish silently, and
// a field nothing reads is legal, cheap and almost always a mistake.
//
// The diff keys on path + title, and score paths carry their index (`scores.0.jsonpath`). Reordering
// scores can therefore make a pre-existing problem look new, or a new one look pre-existing. It errs
// toward NOT blocking, which is the safe direction: the worst case is the operator sees the problem
// as advice instead of a block, and the whole panel is on screen either way.

import { validateDefinition, problemsForPanel, PROBLEM_PANELS, SEVERITY } from '@features/scoreSpec/validate';
import { matchCount, buildSkeleton, parseJsonPath } from '@features/scoreSpec/jsonPath';
import { RESERVED_FEED_COLUMNS } from '@features/scoreSpec/specSchema';
import { pathSegmentsOf, readersOf } from './schemaEdits';

const problemKey = (problem) => `${problem.path}|${problem.title}`;

const outputProblems = (spec) => (spec ? problemsForPanel(validateDefinition(spec), PROBLEM_PANELS.OUTPUT) : []);

export const contractProblems = (builtSpec, sourceSpec) => {
  const inherited = {};
  outputProblems(sourceSpec).forEach((problem) => {
    inherited[problemKey(problem)] = true;
  });

  const fresh = outputProblems(builtSpec).filter((problem) => !inherited[problemKey(problem)]);
  return {
    blocking: fresh.filter((p) => p.severity === SEVERITY.ERROR),
    advice: fresh.filter((p) => p.severity !== SEVERITY.ERROR),
  };
};

// The node a path lands on in a skeleton of the schema, or undefined when it lands nowhere. Used
// only to tell a leaf from an object — `matchCount` counts nodes and cannot.
const nodeAt = (schema, expr) => {
  const segments = parseJsonPath(expr);
  if (!segments || !schema) return undefined;
  let cursor = buildSkeleton(schema);
  for (let i = 0; i < segments.length; i += 1) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    if (!Object.prototype.hasOwnProperty.call(cursor, segments[i])) return undefined;
    cursor = cursor[segments[i]];
  }
  return cursor;
};

const duplicates = (names) => {
  const seen = {};
  const dupes = [];
  names.forEach((name) => {
    if (!name) return;
    if (seen[name] && dupes.indexOf(name) === -1) dupes.push(name);
    seen[name] = true;
  });
  return dupes;
};

// Checks about the editor's own state. `schema` is what `toSchema` produced from the rows, so these
// run against exactly what would be sent.
export const localContractProblems = (model, schema) => {
  const blocking = [];
  const advice = [];

  const namedRows = [];
  const visit = (rows, parentLabel) => {
    (rows || []).forEach((row) => {
      if (!row.name) {
        blocking.push(
          `An answer field${
            parentLabel ? ` inside \`${parentLabel}\`` : ''
          } has no name. Name it or remove it — an unnamed field is dropped from the shape rather than sent.`
        );
      } else {
        namedRows.push(row);
      }
      if (Array.isArray(row.children)) visit(row.children, row.name);
    });
  };
  visit(model.rows, null);

  const siblingDupes = (rows, parentLabel) => {
    duplicates((rows || []).map((r) => r.name)).forEach((name) => {
      blocking.push(
        `Two answer fields${
          parentLabel ? ` inside \`${parentLabel}\`` : ''
        } share the name \`${name}\`. One would overwrite the other.`
      );
    });
    (rows || []).forEach((row) => {
      if (Array.isArray(row.children)) siblingDupes(row.children, row.name);
    });
  };
  siblingDupes(model.rows, null);

  (model.scores || []).forEach((score) => {
    if (!score.jsonpath) {
      blocking.push(`Score \`${score.name || 'unnamed'}\` has no path, so it would extract nothing.`);
      return;
    }
    const node = nodeAt(schema, score.jsonpath);
    if (node !== null && typeof node === 'object') {
      advice.push(
        `\`${score.jsonpath}\` names an object, not a value, so this score would extract a whole object. Nothing checks that — point it at a leaf.`
      );
    }
  });

  (model.extraColumns || []).forEach((column) => {
    if (!column.name && !column.jsonpath) return; // an untouched blank row is not yet a problem
    if (!column.name) blocking.push(`A column reading \`${column.jsonpath}\` has no name.`);
    if (!column.jsonpath) blocking.push(`Column \`${column.name}\` has no path, so it would extract nothing.`);
  });

  duplicates((model.extraColumns || []).map((c) => c.name)).forEach((name) => {
    blocking.push(`Two columns share the name \`${name}\`. One would overwrite the other.`);
  });

  // Not an error anywhere — the column is accepted and then stripped from the payload so it cannot
  // overwrite the point-in-time row column of the same name. Advice, because the loss is silent.
  (model.extraColumns || []).forEach((column) => {
    if (column.name && RESERVED_FEED_COLUMNS.indexOf(column.name) > -1) {
      advice.push(
        `\`${column.name}\` is already a column on every feed, so this one is dropped from the payload rather than allowed to overwrite it. It saves, it locks, and the value never appears — rename it to keep it.`
      );
    }
  });

  namedRows.forEach((row) => {
    const segments = pathSegmentsOf(model.rows, row.id);
    if (!segments) return;
    if (row.type === 'object') return; // its leaves are what get read
    if (readersOf(model, segments).length === 0) {
      advice.push(
        `Nothing extracts \`${row.name}\`. The model will be asked for it and the value will be discarded — add a score or a column that reads it, or remove the field.`
      );
    }
  });

  return { blocking, advice };
};

// The per-row wording, kept identical to the advanced editor's (OutPanel.js:80-92) so the same
// situation does not get described two different ways across the two screens.
export const pathHint = (expr, schema, exactlyOne) => {
  if (!schema) return 'No output_schema declared, so this path cannot be checked here.';
  if (!expr) return null;
  const { supported, matches } = matchCount(expr, schema);
  if (!supported) return 'Outside the path forms this editor can evaluate — the compiler will check it.';
  if (exactlyOne) {
    if (matches === 1) return 'Resolves to exactly one node of the declared shape.';
    return `Resolves to ${matches} nodes. A score path must resolve to exactly one, or extraction fails on every document.`;
  }
  if (matches >= 1) return 'Resolves against the declared shape.';
  return 'Resolves to nothing in the declared shape, so it would be null on every run.';
};

export const isBadPath = (expr, schema, exactlyOne) => {
  if (!schema || !expr) return false;
  const { supported, matches } = matchCount(expr, schema);
  if (!supported) return false;
  return exactlyOne ? matches !== 1 : matches < 1;
};
